import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * A single option item for the Select component.
 */
export interface SelectOption {
  /** The value submitted with the form. */
  value: string;
  /** The human-readable label displayed in the dropdown. */
  label: string;
  /** Whether this option is disabled. */
  disabled?: boolean;
}

/**
 * Props for the Select component.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Label text displayed above the select. */
  label?: string;
  /** Error message. Sets error styling when present. */
  error?: string;
  /** Array of options to render. */
  options: SelectOption[];
  /** Placeholder option shown when no value is selected. */
  placeholder?: string;
  /** Additional CSS classes for the outer wrapper. */
  className?: string;
}

/**
 * Accessible select dropdown with label, error state, and placeholder support.
 * Forwards ref to the underlying select element.
 *
 * @param props.label - Label text
 * @param props.error - Error message (also activates error styling)
 * @param props.options - Array of selectable options
 * @param props.placeholder - Empty/default option label
 * @example
 * ```tsx
 * <Select
 *   label="Category"
 *   placeholder="Select a category"
 *   options={[{ value: 'sport', label: 'Sport' }]}
 *   error="Please select a category"
 * />
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, options, placeholder, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const descriptionId = `${selectId}-description`;
  const hasError = error != null && error.length > 0;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label != null && (
        <label htmlFor={selectId} className="text-sm font-medium text-ink">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-describedby={hasError ? descriptionId : undefined}
        aria-invalid={hasError}
        className={cn(
          'w-full rounded-md border bg-card-elevated px-3 py-2 text-sm text-ink',
          'focus:outline-none focus:ring-2 focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError
            ? 'border-red-500 focus:ring-red-500'
            : 'border-ink/15 focus:ring-[var(--color-primary,theme(colors.blue.500))]',
        )}
        {...props}
      >
        {placeholder != null && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {hasError && (
        <p id={descriptionId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
});
